/**
 * directionPrefill.ts — PLAT-11383 (start from the last direction, and say so
 * when the next one repeats it) and PLAT-11798 (the line a markers editor is
 * offered over).
 *
 * Plain functions: no React, no MobX, no network. Everything here reads a dub
 * manifest that the caller has already fetched.
 *
 * Words used below, each defined once:
 *
 * - a **direction** is a statement of how the dubbed lines should be delivered.
 *   It comes in three forms: a written note, a set of numeric and on/off
 *   controls, and the line's own words with delivery markers written into them.
 * - the **manifest** is the stored record of a language's dub: which line was
 *   voiced by which speaker, from which words, and under which direction.
 * - a **dub line** is one recorded line of that dub.
 */

import { dropSuperseded } from '@src/editor/dubbing/DubPlacementService';

import type {
  DubDirection,
  DubDirectionRecordDTO,
  DubManifestDTO,
} from '@src/repositories/DubbingRepository';

/**
 * The direction the most recent run recorded, or `null` when the dub carries
 * none.
 *
 * "Most recent" is read off the lines themselves: a line names the direction it
 * was recorded under, and it carries the time it was produced. The newest line
 * that names one wins. Reading the directions table alone would not do — the
 * table holds every direction among the dub's lines, unordered, and a dub whose
 * lines were directed at different times would give an arbitrary answer.
 *
 * Why the previous run at all: a direction states the complete delivery wanted
 * and REPLACES the one before it. A person can only meaningfully replace
 * something they can see, so the controls start where the last run left them
 * (settled 2026-09-20).
 */
export function previousDirection(
  manifest: DubManifestDTO | null | undefined,
  segmentIds?: readonly string[],
): DubDirection | null {
  return runPreviousDirectionRecord(manifest, segmentIds)?.direction ?? null;
}

/**
 * The record a run should start from.
 *
 * A run covering exactly ONE line starts from that line's own last direction,
 * not from the newest one anywhere in the dub. Otherwise a run over one
 * speaker's line would open holding another speaker's marked-up words — which
 * for the markers editor means literally putting one person's words into
 * another's mouth, since marked-up text IS the line's words.
 *
 * A wider run has no single line to ask, so it falls back to the newest
 * direction the dub carries.
 */
export function runPreviousDirectionRecord(
  manifest: DubManifestDTO | null | undefined,
  segmentIds?: readonly string[],
): DubDirectionRecordDTO | null {
  const onlyLine = segmentIds?.length === 1 ? segmentIds[0] : undefined;
  if (onlyLine !== undefined) {
    return previousDirectionRecordForLine(manifest, onlyLine);
  }
  return previousDirectionRecord(manifest);
}

/** The direction one named line was last recorded under. */
export function previousDirectionRecordForLine(
  manifest: DubManifestDTO | null | undefined,
  segmentId: string,
): DubDirectionRecordDTO | null {
  const directions = manifest?.directions;
  if (!manifest || !directions) return null;
  const segment = (manifest.segments ?? []).find(
    s => s.segmentId === segmentId,
  );
  const id = segment?.directionId;
  return (id && directions[id]) || null;
}

/** The whole stored record, for callers that also want how it resolved. */
export function previousDirectionRecord(
  manifest: DubManifestDTO | null | undefined,
): DubDirectionRecordDTO | null {
  const directions = manifest?.directions;
  if (!manifest || !directions) return null;
  let best: { record: DubDirectionRecordDTO; at: number } | null = null;
  for (const segment of manifest.segments ?? []) {
    const id = segment.directionId;
    if (!id) continue;
    const record = directions[id];
    if (!record) continue;
    // A line with no recorded time still counts, as the oldest thing there is:
    // it is a real direction, and preferring nothing to it would lose it.
    const at = segment.producedAt ? Date.parse(segment.producedAt) : 0;
    const usable = Number.isNaN(at) ? 0 : at;
    if (!best || usable >= best.at) best = { record, at: usable };
  }
  return best?.record ?? null;
}

/**
 * The numeric and on/off values a previous `config` direction carried, or
 * `null` when the previous run was directed some other way (or not at all).
 *
 * Only values the panel can actually show come back: a stored value that is
 * neither a number nor an on/off is dropped rather than rendered as text.
 */
export function previousKnobValues(
  direction: DubDirection | null,
): Record<string, number | boolean> | null {
  if (!direction || direction.kind !== 'config') return null;
  const values: Record<string, number | boolean> = {};
  for (const [name, value] of Object.entries(direction.settings)) {
    if (typeof value === 'number' || typeof value === 'boolean') {
      values[name] = value;
    }
  }
  return Object.keys(values).length > 0 ? values : null;
}

/** One line the markers editor covers: its id, words, and last direction. */
export interface MarkersLine {
  segmentId: string;
  /** Who says it (internal `spk_X` label). */
  speaker: string;
  /** The words the line was last voiced from. */
  text: string;
  /** The direction THIS line was last recorded under, or `null`. */
  previous: DubDirection | null;
}

/**
 * The lines a markers editor covers, in the order they are spoken
 * (PLAT-12198), each with the words it was last voiced from and the direction
 * it was last recorded under — its own, so each line starts from its own
 * markup.
 *
 * `null` when the run is not a span the editor may be offered over (see
 * {@link markersCoverSpan}), or when the manifest has no recording of one of
 * its lines: markup is written into a line's words, and a line with no
 * recorded words has nothing to write into.
 */
export function markersSpanLines(
  manifest: DubManifestDTO | null | undefined,
  segmentIds: readonly string[] | undefined,
): MarkersLine[] | null {
  if (!manifest || !segmentIds || !markersCoverSpan(segmentIds)) return null;
  const bySegment = new Map(
    (manifest.segments ?? []).map(s => [s.segmentId, s]),
  );
  const lines: (MarkersLine & { at: number })[] = [];
  for (const segmentId of segmentIds) {
    const segment = bySegment.get(segmentId);
    const text = segment?.sourceTranslationSnapshot;
    if (!segment || text == null) return null;
    // The direction of the recording the words came from, not another one.
    const record = segment.directionId
      ? manifest.directions?.[segment.directionId]
      : undefined;
    lines.push({
      segmentId,
      speaker: segment.speaker,
      text,
      previous: record?.direction ?? null,
      at: segment.originalStartMs,
    });
  }
  return lines.sort((a, b) => a.at - b.at).map(({ at: _at, ...line }) => line);
}

/**
 * Whether transcript labels all belong to one person. `personOf` maps a label
 * to the person behind it, since one person can carry two labels; a label it
 * does not know stands for itself (PLAT-12198).
 */
export function isOnePerson(
  labels: readonly string[],
  personOf: (label: string) => string | undefined = () => undefined,
): boolean {
  return new Set(labels.map(label => personOf(label) ?? label)).size <= 1;
}

/**
 * Whether the lines are all said by one speaker. Markup is a passage's own
 * words directed for one voice, so the editor is offered over one speaker's
 * lines only (PLAT-12198), the same rule as the selection popover's.
 */
export function markersOneSpeaker(
  lines: readonly MarkersLine[],
  personOf?: (label: string) => string | undefined,
): boolean {
  return isOnePerson(
    lines.map(line => line.speaker),
    personOf,
  );
}

/**
 * The marked-up text a direction carries for one line, or `null` when it
 * carries none. A `tags` direction is one line's markup by definition, so it
 * answers for whichever line was recorded under it; `tags-by-line` answers for
 * the lines it names (PLAT-12198).
 */
export function taggedTextForLine(
  direction: DubDirection | null | undefined,
  segmentId: string,
): string | null {
  if (direction?.kind === 'tags') return direction.taggedText;
  if (direction?.kind === 'tags-by-line') {
    return direction.taggedTextByLine[segmentId] ?? null;
  }
  return null;
}

/**
 * The written note a previous run carried, or `null` when the previous run was
 * directed some other way (or not at all).
 *
 * There is no "model default" for prose, so a run with no previous note starts
 * with an empty field. The document asks for the note to be pre-filled in the
 * same breath as the controls (requirement P8 and section 8.3, "free-form text
 * field, pre-filled with last note"), for the same reason: a direction states
 * the complete delivery wanted and replaces the one before it, and a person can
 * only meaningfully replace what they can see.
 */
export function previousNote(direction: DubDirection | null): string | null {
  return direction?.kind === 'free-form' ? direction.note : null;
}

/**
 * Remove delivery markers from a line, leaving the words underneath.
 *
 * Only used to answer "are these still this line's words?". Markers are written
 * in square brackets, so everything bracketed comes out and the remaining
 * spacing is collapsed. A line whose real words genuinely contain square
 * brackets would read as changed; the cost of that is the previous markers not
 * being restored, which is the safe direction to be wrong in.
 */
export function stripDeliveryMarkers(text: string): string {
  return text
    .replace(/\[[^\]]*\]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** What the markers editor opens with, and why. */
export interface MarkersSeed {
  /** The text to put in the box; `null` when there is nothing to offer. */
  text: string | null;
  /** True when the previous run's marked-up text was put back. */
  restoredFromPreviousRun: boolean;
  /**
   * True when a previous run's marked-up text existed but was NOT put back,
   * because the words underneath it are no longer this line's words. The person
   * is told, rather than left to notice their markers have gone.
   */
  staleMarkersDropped: boolean;
}

/**
 * What the delivery-markers editor opens with.
 *
 * A direction is ABSOLUTE: it states the delivery wanted, complete, and
 * replaces the one before it. Section 3.2 spells out what that means here —
 * "the editor is seeded with the previous run's tagged text, and removing a tag
 * removes it". Opening with the plain words instead would silently throw away
 * the markers written last time, which is the exact thing that rule forbids.
 *
 * The one case the document does not settle is a stale one. Marked-up text IS
 * the line's words with markers written among them, so if the line has since
 * been voiced from different words the stored markup carries words that are no
 * longer there. Putting it back would hand someone old words to re-record, and
 * a person who pressed Generate would hear the dub quietly revert to them. So
 * the markup is dropped and the line's own words are offered instead — the
 * words are always right, and the markers are the recoverable loss.
 *
 * `lineText` is the words the line was last voiced from, which is the only
 * record of that line's words the dub itself holds.
 */
export function markersSeed(input: {
  previous: DubDirection | null;
  lineText: string | null;
  /** The line being seeded; reads that line's markup out of `tags-by-line`. */
  lineId?: string;
}): MarkersSeed {
  const { previous, lineText, lineId } = input;
  const markup = taggedTextForLine(previous, lineId ?? '');
  if (markup === null) {
    return {
      text: lineText,
      restoredFromPreviousRun: false,
      staleMarkersDropped: false,
    };
  }
  const wordsUnderTheMarkers = stripDeliveryMarkers(markup);
  const wordsNow = lineText === null ? null : stripDeliveryMarkers(lineText);
  if (wordsNow !== null && wordsUnderTheMarkers !== wordsNow) {
    return {
      text: lineText,
      restoredFromPreviousRun: false,
      staleMarkersDropped: true,
    };
  }
  return {
    text: markup,
    restoredFromPreviousRun: true,
    staleMarkersDropped: false,
  };
}

/**
 * The most lines one markers editor covers (PLAT-12198). The backend holds a
 * `tags-by-line` direction to the same number.
 */
export const MAX_MARKED_LINES = 10;

/**
 * Whether a markers editor may be offered for this run's span at all.
 *
 * Delivery markers are a line's own words with markup written into them. One
 * line sends them as `tags`; up to {@link MAX_MARKED_LINES} lines send each
 * line's markup keyed by its id (`tags-by-line`, PLAT-12198). A wider span is
 * refused by the backend before any audio is made, so the screen must not
 * offer the editor for one — a person should never be able to write something
 * that will be thrown away (settled 2026-09-20).
 *
 * A whole-language run (no line ids at all) covers every line, so it is a wider
 * selection too.
 */
export function markersCoverSpan(
  segmentIds: readonly string[] | undefined,
): boolean {
  return (
    !!segmentIds &&
    segmentIds.length >= 1 &&
    segmentIds.length <= MAX_MARKED_LINES
  );
}

/**
 * The short string that stands for a direction, used only to tell two
 * directions apart.
 *
 * A hand-copied twin of the backend's `canonicalDirectionKey`
 * (`lib/dubbing/direction.ts`), and it must stay one: the two answers are
 * compared by a person, not by the machine, so a drift between them shows up as
 * a warning that appears when it should not. Whitespace is normalized for the
 * same reason the backend normalizes it — a re-wrapped line is a cosmetic edit.
 * Everything else, including capitals and punctuation, is left exactly as
 * written, because all of it changes how a line is read.
 */
export function directionFingerprint(direction: DubDirection): string {
  switch (direction.kind) {
    case 'free-form':
      return `free-form:${normalizeSpace(direction.note)}`;
    case 'config': {
      const entries = Object.entries(direction.settings)
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
        .map(([k, v]) => `${k}=${String(v)}`);
      return `config:${entries.join(',')}`;
    }
    case 'tags':
      return `tags:${normalizeSpace(direction.taggedText)}`;
    case 'tags-by-line': {
      const entries = Object.entries(direction.taggedTextByLine)
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
        .map(([line, text]) => [line, normalizeSpace(text)]);
      return `tags-by-line:${JSON.stringify(entries)}`;
    }
  }
}

function normalizeSpace(text: string): string {
  return text.trim().replace(/\s+/g, ' ');
}

/**
 * Whether the direction about to be sent is the very same one the last run was
 * given.
 *
 * The warning this answers is NOT "you will get the audio you already have".
 * Every directed run is recorded as its own attempt, so re-issuing the same
 * direction produces a genuinely new read — it just produces another read of
 * the same direction, which is rarely what a person means to ask for. The
 * wording on screen says that, and this function only decides when to say it.
 */
export function repeatsPreviousDirection(
  next: DubDirection | undefined,
  previous: DubDirection | null,
): boolean {
  if (!next || !previous) return false;
  return directionFingerprint(next) === directionFingerprint(previous);
}

/**
 * PLAT-12023 — the words a speaker's preview plays: their lines inside the
 * run's span, in order, cut short.
 *
 * A preview is one provider call per press, so it is kept to a short span: the
 * text is cut at the first word boundary after `maxChars` characters. For a
 * whole-language run (no line ids) the speaker's lines are read from the whole
 * dub, so the preview is still their own words rather than a stock phrase.
 *
 * `speakerLabels` are every transcript label of one person (PLAT-12198): a
 * passage can mix them, and the words must cover the same lines as its tags.
 *
 * `null` when the dub has no line for this speaker in that span — there is
 * nothing to play but the stock sample, and the caller says so.
 */
export function previewSpanText(
  manifest: DubManifestDTO | null | undefined,
  segmentIds: readonly string[] | undefined,
  speakerLabels: readonly string[],
  maxChars = 160,
): string | null {
  if (!manifest) return null;
  const wanted = segmentIds?.length ? new Set(segmentIds) : null;
  const lines = dropSuperseded(manifest.segments ?? [])
    .live.filter(
      segment =>
        speakerLabels.includes(segment.speaker) &&
        (!wanted || wanted.has(segment.segmentId)),
    )
    .sort((a, b) => a.originalStartMs - b.originalStartMs)
    .map(segment => segment.sourceTranslationSnapshot?.trim() ?? '')
    .filter(Boolean);
  if (lines.length === 0) return null;
  return cutAtWord(lines.join(' '), maxChars);
}

/** The text up to the first space after `maxChars`, or all of it. */
function cutAtWord(text: string, maxChars: number): string {
  if (text.length <= maxChars) return text;
  const boundary = text.indexOf(' ', maxChars);
  return boundary === -1 ? text : text.slice(0, boundary);
}
